// IdleTimerProvider.jsx
import React, { useEffect, useRef } from "react";

export default function IdleTimerProvider({
  idleMs = 15 * 60 * 1000,       // 기본 15분
  warnMs = 60 * 1000,            // 타임아웃 전 경고
  onWarn,
  onTimeout,
  syncAcrossTabs = true,
  resetOnTimeout = true,         // ✅ 타임아웃 시 자동 리셋 (기본 활성)
  enabled = true,                // ✅ 추가: 로그인 상태 등으로 활성/비활성 제어
  children,
}) {

  const expireTimer = useRef(null);
  const warnTimer = useRef(null);
  const bcRef = useRef(null);
  const lastResetAt = useRef(0);
  const lastActivityTime = useRef(Date.now()); // 마지막 활동 시간 기록

  const clearTimers = () => {
    if (expireTimer.current) clearTimeout(expireTimer.current);
    if (warnTimer.current) clearTimeout(warnTimer.current);
  };

  const reset = () => {
    // enabled가 false면 아예 동작 안 하도록 막기
    if (!enabled) return;

    // console.log("%c[IdleTimer] RESET()", "color: #4CAF50; font-weight: bold;");
    lastActivityTime.current = Date.now();

    clearTimers();

    if (onWarn && warnMs < idleMs) {
      warnTimer.current = setTimeout(() => {
        // console.log("%c[IdleTimer] WARN trigger", "color: orange; font-weight: bold;");
        onWarn(idleMs - warnMs);
      }, idleMs - warnMs);
    }

    expireTimer.current = setTimeout(() => {
      // console.log("%c[IdleTimer] TIMEOUT TRIGGERED", "color: red; font-weight: bold;");

      if (syncAcrossTabs && bcRef.current) {
        bcRef.current.postMessage({ type: "IDLE_TIMEOUT" });
      }

      onTimeout && onTimeout();

      // ✅ 자동 재시작은 "여전히 enabled일 때만"
      if (resetOnTimeout && enabled) {
        reset();
      }
    }, idleMs);
  };

  const handleActivity = (event) => {
    // 비활성 상태면 이벤트 무시
    if (!enabled) return;

    const now = Date.now();
    if (now - lastResetAt.current < 250) return; // throttle

    // console.log(
    //   `%c[IdleTimer] Activity detected: ${event.type}`,
    //   "color: #1976D2; font-weight: bold;"
    // );

    lastResetAt.current = now;
    lastActivityTime.current = now;
    reset();

    if (syncAcrossTabs && bcRef.current) {
      bcRef.current.postMessage({ type: "IDLE_RESET" });
    }
  };

  useEffect(() => {
    // console.log(
    //   `%c[IdleTimer] useEffect → enabled = ${enabled}`,
    //   "color: purple; font-weight: bold;"
    // );

    // 🔴 enabled가 false일 땐 아무 것도 안 하고, 기존 타이머만 정리
    if (!enabled) {
      clearTimers();
      bcRef.current?.close();
      return;
    }

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "wheel",
      "touchstart",
      "scroll",
      "visibilitychange",
    ];

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityTime.current) / 1000);
      // console.log(`%c[IdleTimer] elapsed idle time: ${elapsed} sec`, "color: #009688;");
    }, 1000);

    if (syncAcrossTabs && "BroadcastChannel" in window) {
      bcRef.current = new BroadcastChannel("idle-sync");
      bcRef.current.onmessage = (e) => {
        if (!e?.data) return;

        // console.log(
        //   `%c[IdleTimer] Message received: ${e.data.type}`,
        //   "color: #673AB7;"
        // );

        if (e.data.type === "IDLE_TIMEOUT") {
          onTimeout && onTimeout();
          if (resetOnTimeout && enabled) reset();
        }
        if (e.data.type === "IDLE_RESET") {
          reset();
        }
      };
    }

    events.forEach((ev) =>
      window.addEventListener(ev, handleActivity, { passive: true })
    );

    // ✅ enabled가 true일 때만 첫 타이머 시작
    reset();

    return () => {
      // console.log("%c[IdleTimer] Cleanup", "color: gray;");
      clearInterval(interval);
      events.forEach((ev) => window.removeEventListener(ev, handleActivity));
      clearTimers();
      bcRef.current?.close();
    };
  }, [idleMs, warnMs, syncAcrossTabs, resetOnTimeout, enabled]); // ✅ enabled 추가

  return <>{children}</>;
}
